// @ts-nocheck
import React from 'react';
import styled from 'styled-components';
import classNames from 'classnames';
import { Form, useFormikContext } from 'formik';
import { Button, Classes, Dialog, FormGroup, Intent } from '@blueprintjs/core';
import { TimezonePicker } from '@blueprintjs/timezone';
import { ErrorMessage, FastField } from 'formik';
import { useHistory } from 'react-router-dom';
import intl from 'react-intl-universal';

import {
  FieldRequiredHint,
  FormattedMessage as T,
  FFormGroup,
  FInputGroup,
  FSelect,
  Row,
  Stack,
  Col,
  Paper,
  CardFooterActions,
  AppToaster,
} from '@/components';
import { Checkbox } from '@blueprintjs/core';
import { inputIntent } from '@/utils';
import { CLASSES } from '@/constants/classes';
import { getAllCurrenciesOptions } from '@/constants/currencies';
import { getFiscalYear } from '@/constants/fiscalYearOptions';
import { getLanguages } from '@/constants/languagesOptions';
import { useGeneralFormContext } from './GeneralFormProvider';
import { getAllCountries } from '@/utils/countries';
import { shouldBaseCurrencyUpdate } from './utils';
import { UpdateLogoButton } from '@/containers/UpdateLogo/UpdateLogoButton';
import { getZatcaMode } from '@/constants/baseUrlOptions';

const Countries = getAllCountries();

const normalizeZatcaValues = (values) => ({
  ...values,
  zatca_sandbox_url:
    values.zatca_mode === 'sandbox' ? values.zatca_sandbox_url : null,
  zatca_production_url:
    values.zatca_mode === 'production' ? values.zatca_production_url : null,
});

const ZATCA_REQUIRED_FIELDS = [
  'uuid',
  'custom_id',
  'model',
  'location_city',
  'location_city_subdivision',
  'location_street',
  'location_building',
  'location_plot_identification',
  'location_postal_zone',
];

const getZatcaRequiredFields = (values) => [
  ...ZATCA_REQUIRED_FIELDS,
];

const isBlankValue = (value) =>
  value === null || value === undefined || `${value}`.trim() === '';

/**
 * Preferences general form.
 */
export default function PreferencesGeneralForm({ isSubmitting }) {
 
  const history = useHistory();
  const { values, setSubmitting, setTouched, validateForm } = useFormikContext();
  const [isZatcaDialogOpen, setZatcaDialogOpen] = React.useState(false);

  const FiscalYear = getFiscalYear();
  const Languages = getLanguages();
  const Currencies = getAllCurrenciesOptions();
  const ZatcaMode = getZatcaMode();

  const {
    dateFormats,
    baseCurrencyMutateAbility,
    updateOrganization,
    isZatcaOnboarding,
  } = useGeneralFormContext();

  const baseCurrencyDisabled = baseCurrencyMutateAbility.length > 0;
  const zatcaRequiredFields = React.useMemo(
    () => getZatcaRequiredFields(values),
    [values.zatca_mode]
  );
  const
    isZatcaOnboardDisabled = zatcaRequiredFields.some((field) =>
      isBlankValue(values[field])
    );

  // Handle close click.
  const handleCloseClick = () => {
    history.go(-1);
  };

  const touchZatcaFields = () => {
    setTouched(
      zatcaRequiredFields.reduce(
        (acc, field) => ({
          ...acc,
          [field]: true,
        }),
        {}
      ),
      false
    );
    validateForm();
  };

  React.useEffect(() => {
    if (isZatcaDialogOpen) {
      touchZatcaFields();
    }
  }, [isZatcaDialogOpen, zatcaRequiredFields]);

  const handleZatcaSettingsClick = () => {
    setZatcaDialogOpen(true);
    touchZatcaFields();
  };

  const handleZatcaOnboardClick = async () => {
    const errors = await validateForm();
    const hasZatcaErrors = zatcaRequiredFields.some((field) => errors[field]);

    if (hasZatcaErrors || isZatcaOnboardDisabled) {
      touchZatcaFields();
      return;
    }

    setSubmitting(true);
    try {
      await updateOrganization(normalizeZatcaValues(values));
      AppToaster.show({
        message: intl.get('preferences.general.success_message'),
        intent: Intent.SUCCESS,
      });
      setZatcaDialogOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Form>
      <PreferencesContainer>
        <Row style={{ justifyContent: 'space-around' }}>
          <CustomCol xs={3}>
            <Stack spacing={20} width={'300px'}>
              <UpdateLogoButton />

              {/* ---------- Organization name ----------  */}
              <FFormGroup
                name={'name'}
                label={<T id={'organization_name'} />}
                labelInfo={<FieldRequiredHint />}
                inline={false}
                helperText={<T id={'shown_on_sales_forms_and_purchase_orders'} />}
                fastField={true}
              >
                <FInputGroup medium={'true'} name={'name'} fastField={true} />
              </FFormGroup>

              {/* ---------- Location ---------- */}
              <FFormGroup
                name={'location'}
                label={<T id={'business_location'} />}
                inline={false}
                fastField={true}
              >
                <FSelect
                  name={'location'}
                  items={Countries}
                  valueAccessor={'countryCode'}
                  labelAccessor={'countryCode'}
                  textAccessor={'name'}
                  placeholder={<T id={'select_business_location'} />}
                  popoverProps={{ minimal: true }}
                  fastField={true}
                />
              </FFormGroup>
            </Stack>
          </CustomCol>

          <CustomCol xs={3}>
            <Stack spacing={20} width={'300px'}>
              {/* ----------  Base currency ----------  */}
              <FFormGroup
                name={'base_currency'}
                baseCurrencyDisabled={baseCurrencyDisabled}
                label={<T id={'base_currency'} />}
                labelInfo={<FieldRequiredHint />}
                inline={false}
                helperText={
                  <T
                    id={
                      'you_can_t_change_the_base_currency_as_there_are_transactions'
                    }
                  />
                }
                fastField={true}
                shouldUpdate={shouldBaseCurrencyUpdate}
              >
                <FSelect
                  name={'base_currency'}
                  items={Currencies}
                  valueAccessor={'key'}
                  textAccessor={'name'}
                  labelAccessor={'key'}
                  placeholder={<T id={'select_base_currency'} />}
                  popoverProps={{ minimal: true }}
                  disabled={baseCurrencyDisabled}
                  fastField={true}
                  shouldUpdate={shouldBaseCurrencyUpdate}
                  baseCurrencyDisabled={baseCurrencyDisabled}
                />
              </FFormGroup>

              {/* --------- Fiscal Year ----------- */}
              <FFormGroup
                name={'fiscal_year'}
                label={<T id={'fiscal_year'} />}
                labelInfo={<FieldRequiredHint />}
                inline={false}
                helperText={
                  <T id={'for_reporting_you_can_specify_any_month'} />
                }
                fastField={true}
              >
                <FSelect
                  name={'fiscal_year'}
                  items={FiscalYear}
                  valueAccessor={'key'}
                  textAccessor={'name'}
                  placeholder={<T id={'select_fiscal_year'} />}
                  popoverProps={{ minimal: true }}
                  fastField={true}
                />
              </FFormGroup>

              {/* --------- Data format ----------- */}
              <FFormGroup
                name={'date_format'}
                label={<T id={'date_format'} />}
                labelInfo={<FieldRequiredHint />}
                inline={false}
                helperText={<ErrorMessage name="date_format" />}
                fastField={true}
              >
                <FSelect
                  name={'date_format'}
                  items={dateFormats}
                  valueAccessor={'key'}
                  textAccessor={'label'}
                  placeholder={<T id={'select_date_format'} />}
                  popoverProps={{ minimal: true }}
                  fastField={true}
                />
              </FFormGroup>
            </Stack>
          </CustomCol>

          <CustomCol xs={3}>
            <Stack spacing={20} width={'300px'}>
              {/* ---------- Language ---------- */}
              <FormGroup
                name={'language'}
                label={<T id={'language'} />}
                labelInfo={<FieldRequiredHint />}
                inline={false}
                fastField={true}
              >
                <FSelect
                  name={'language'}
                  items={Languages}
                  valueAccessor={'value'}
                  textAccessor={'name'}
                  placeholder={<T id={'select_language'} />}
                  popoverProps={{ minimal: true }}
                  fastField={true}
                />
              </FormGroup>

              {/* ----------  Time zone ----------  */}
              <FastField name={'timezone'}>
                {({ form, field: { value }, meta: { error, touched } }) => (
                  <FormGroup
                    label={<T id={'time_zone'} />}
                    labelInfo={<FieldRequiredHint />}
                    inline={false}
                    className={classNames(
                      'form-group--time-zone',
                      CLASSES.FORM_GROUP_LIST_SELECT,
                      CLASSES.FILL,
                    )}
                    intent={inputIntent({ error, touched })}
                    helperText={<ErrorMessage name="timezone" />}
                  >
                    <TimezonePicker
                      value={value}
                      onChange={(timezone) => {
                        form.setFieldValue('timezone', timezone);
                      }}
                      valueDisplayFormat="composite"
                      placeholder={<T id={'select_time_zone'} />}
                    />
                  </FormGroup>
                )}
              </FastField>



              {/* ---------- User Type ---------- */}
             <FastField name="user_type">
  {({ form, field: { value } }) => (
    <FormGroup inline={false} style={{ marginBottom: '0px' }}>
      <Checkbox
        checked={value === 'dropship'}
        onChange={(e) =>
          form.setFieldValue(
            'user_type',
            e.target.checked ? 'dropship' : 'inventory'
          )
        }
      >
        Is Dropship
      </Checkbox>
    </FormGroup>
  )}
</FastField>

<FastField name="zatca_on">
  {({ form, field: { value } }) => (
    <FormGroup inline={false} style={{ marginBottom: 0 }}>
      <Checkbox
        checked={!!value}
        onChange={(e) =>
          form.setFieldValue('zatca_on', e.target.checked)
        }
      >
        ZATCA On
      </Checkbox>
    </FormGroup>
  )}
</FastField>


              {Boolean(values.zatca_on) && (
                <Button
                  type="button"
                  intent={Intent.PRIMARY}
                  onClick={handleZatcaSettingsClick}
                >
                  <T id={'zatca_settings'} />
                </Button>
              )}
            </Stack>
          </CustomCol>
        </Row>
      </PreferencesContainer>

      <Dialog
        isOpen={isZatcaDialogOpen}
        onClose={() => setZatcaDialogOpen(false)}
        title={<T id={'zatca_settings'} />}
      >
        <ZatcaDialogBody className={Classes.DIALOG_BODY}>
          <ZatcaFieldsGrid>
            <FormGroup
              name={'zatca_mode'}
              label={<T id={'zatca_mode'} />}
              labelInfo={<FieldRequiredHint />}
              inline={false}
              helperText={<ErrorMessage name="zatca_mode" />}
              fastField={true}
            >
              <FSelect
                name={'zatca_mode'}
                items={ZatcaMode}
                valueAccessor={'value'}
                textAccessor={'name'}
                placeholder={<T id={'select_zatca_mode'} />}
                popoverProps={{ minimal: true }}
                fastField={true}
              />
            </FormGroup>

            <FFormGroup
              name={'name'}
              label={<T id={'organization_name'} />}
              labelInfo={<FieldRequiredHint />}
              inline={false}
              helperText={<ErrorMessage name="name" />}
              fastField={true}
            >
              <FInputGroup name={'name'} medium={'true'} fastField={true} />
            </FFormGroup>

            <FFormGroup
              name={'tax_number'}
              label={<T id={'organization_tax_number'} />}
              labelInfo={<FieldRequiredHint />}
              inline={false}
              helperText={<ErrorMessage name="tax_number" />}
              fastField={true}
            >
              <FInputGroup
                name={'tax_number'}
                medium={'true'}
                fastField={true}
              />
            </FFormGroup>

            <FFormGroup
              name={'CRN_number'}
              label={<T id={'crn_number'} />}
              inline={false}
              helperText={<ErrorMessage name="CRN_number" />}
              fastField={true}
            >
              <FInputGroup
                name={'CRN_number'}
                medium={'true'}
                fastField={true}
              />

              
            </FFormGroup>

            <FFormGroup
              name={'zatca_otp'}
              label={<T id={'zatca_otp'} />}
              inline={false}
              helperText={<ErrorMessage name="zatca_otp" />}
              fastField={true}
            >
              <FInputGroup
                name={'zatca_otp'}
                medium={'true'}
                fastField={true}
              />
            </FFormGroup>

            <FFormGroup
              name={'industry'}
              label={<T id={'organization_industry'} />}
              inline={false}
              helperText={<ErrorMessage name="industry" />}
              fastField={true}
            >
              <FInputGroup name={'industry'} medium={'true'} fastField={true} />
            </FFormGroup>

            <FFormGroup
              name={'location'}
              label={<T id={'business_location'} />}
              inline={false}
              helperText={<ErrorMessage name="location" />}
              fastField={true}
            >
              <FSelect
                name={'location'}
                items={Countries}
                valueAccessor={'countryCode'}
                labelAccessor={'countryCode'}
                textAccessor={'name'}
                placeholder={<T id={'select_business_location'} />}
                popoverProps={{ minimal: true }}
                fastField={true}
              />
            </FFormGroup>

            <FFormGroup
              name={'uuid'}
              label={<T id={'uuid'} />}
              labelInfo={<FieldRequiredHint />}
              inline={false}
              helperText={<ErrorMessage name="uuid" />}
              fastField={true}
            >
              <FInputGroup name={'uuid'} medium={'true'} fastField={true} />
            </FFormGroup>

            <FFormGroup
              name={'custom_id'}
              label={<T id={'custom_id'} />}
              inline={false}
              helperText={<ErrorMessage name="custom_id" />}
              fastField={true}
            >
              <FInputGroup
                name={'custom_id'}
                medium={'true'}
                fastField={true}
              />
            </FFormGroup>

            <FFormGroup
              name={'model'}
              label={<T id={'model'} />}
              inline={false}
              helperText={<ErrorMessage name="model" />}
              fastField={true}
            >
              <FInputGroup name={'model'} medium={'true'} fastField={true} />
            </FFormGroup>

            <FFormGroup
              name={'location_city'}
              label={<T id={'city'} />}
              inline={false}
              helperText={<ErrorMessage name="location_city" />}
              fastField={true}
            >
              <FInputGroup
                name={'location_city'}
                medium={'true'}
                fastField={true}
              />
            </FFormGroup>

            <FFormGroup
              name={'location_city_subdivision'}
              label={<T id={'city_subdivision'} />}
              inline={false}
              helperText={<ErrorMessage name="location_city_subdivision" />}
              fastField={true}
            >
              <FInputGroup
                name={'location_city_subdivision'}
                medium={'true'}
                fastField={true}
              />
            </FFormGroup>

            <FFormGroup
              name={'location_street'}
              label={<T id={'street'} />}
              inline={false}
              helperText={<ErrorMessage name="location_street" />}
              fastField={true}
            >
              <FInputGroup
                name={'location_street'}
                medium={'true'}
                fastField={true}
              />
            </FFormGroup>

            <FFormGroup
              name={'location_building'}
              label={<T id={'building'} />}
              inline={false}
              helperText={<ErrorMessage name="location_building" />}
              fastField={true}
            >
              <FInputGroup
                name={'location_building'}
                medium={'true'}
                fastField={true}
              />
            </FFormGroup>

            <FFormGroup
              name={'location_plot_identification'}
              label={<T id={'plot_identification'} />}
              inline={false}
              helperText={<ErrorMessage name="location_plot_identification" />}
              fastField={true}
            >
              <FInputGroup
                name={'location_plot_identification'}
                medium={'true'}
                fastField={true}
              />
            </FFormGroup>

            <FFormGroup
              name={'location_postal_zone'}
              label={<T id={'postal_zone'} />}
              inline={false}
              helperText={<ErrorMessage name="location_postal_zone" />}
              fastField={true}
            >
              <FInputGroup
                name={'location_postal_zone'}
                medium={'true'}
                fastField={true}
              />
            </FFormGroup>

          </ZatcaFieldsGrid>
        </ZatcaDialogBody>

        <div className={Classes.DIALOG_FOOTER}>
          <div className={Classes.DIALOG_FOOTER_ACTIONS}>
            <Button type="button" onClick={() => setZatcaDialogOpen(false)}>
              <T id={'close'} />
            </Button>
            <Button
              type="button"
              intent={Intent.PRIMARY}
              loading={isZatcaOnboarding || isSubmitting}
              disabled={isZatcaOnboardDisabled}
              onClick={handleZatcaOnboardClick}
            >
              <T id={'zatca_onboard'} />
            </Button>
          </div>
        </div>
      </Dialog>

      <CardFooterActions>
        <Button onClick={handleCloseClick}>
          <T id={'close'} />
        </Button>
        <Button loading={isSubmitting} intent={Intent.PRIMARY} type="submit">
          <T id={'save'} />
        </Button>
      </CardFooterActions>
    </Form>
  );
}

const PreferencesContainer = styled(Paper)`
  padding: 10px 10px 20px 0px;
  flex: 1 0;
  border: none; !important
`;

const CustomCol = styled(Col)`
  padding: 0 0 0 20px !important;
`;

const ZatcaDialogBody = styled.div`
  width: 760px;
  max-width: calc(100vw - 80px);
  max-height: 70vh;
  overflow-y: auto;
`;

const ZatcaFieldsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px 24px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;
